'use client';

import Link from 'next/link';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { V4GhostButton, SectLabel } from '@/components/landing/v4-shared';
import { SIMPLE_SETUP_COPY } from '@/lib/marketing/messaging-bank';
import { HERO_COPY } from '@/lib/simple-setup/customer-wins';

/**
 * V4 cyan-pillar hero for /simple-setup.
 *
 * - SectLabel eyebrow (geist-mono).
 * - <h1> Geist Sans 500, clamp(40px,7vw,96px), -0.035em, lh 1.02.
 *   The pillar accent word "on" is wrapped in Instrument Serif italic.
 * - V4-styled magnetic primary CTA + V4GhostButton secondary (cyan).
 * - Magnetic cursor on the primary CTA (60px radius).
 * - Tether beam from the primary CTA down toward #how-it-works.
 */
export function SimpleSetupHero() {
  const primaryCtaRef = useRef<HTMLAnchorElement | null>(null);

  // Magnetic cursor — primary CTA leans toward the cursor inside a 60px radius.
  useEffect(() => {
    const btn = primaryCtaRef.current;
    if (!btn) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    const onMove = (e: PointerEvent) => {
      const rect = btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const radius = 60;
      if (dist > rect.width / 2 + radius) {
        btn.style.setProperty('--mag-x', '0px');
        btn.style.setProperty('--mag-y', '0px');
        return;
      }
      const pull = Math.min(1, 1 - dist / (rect.width / 2 + radius));
      btn.style.setProperty('--mag-x', `${dx * pull * 0.18}px`);
      btn.style.setProperty('--mag-y', `${dy * pull * 0.18}px`);
    };
    const onLeave = () => {
      btn.style.setProperty('--mag-x', '0px');
      btn.style.setProperty('--mag-y', '0px');
    };

    window.addEventListener('pointermove', onMove);
    btn.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      btn.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  return (
    <section
      className="relative isolate overflow-hidden"
      style={{ minHeight: '92vh', display: 'flex', alignItems: 'center' }}
    >
      <div className="v4-sp-hero-aurora" aria-hidden="true" />
      <div className="v4-sp-grid-overlay" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[1200px] px-6 py-24 text-center">
        <div className="v4-sp-rise-1 mb-10" style={{ display: 'inline-flex', justifyContent: 'center' }}>
          <SectLabel color="var(--cyan-glow)">{SIMPLE_SETUP_COPY.eyebrow}</SectLabel>
        </div>

        <h1
          className="speakable-hero v4-sp-rise-2"
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontWeight: 500,
            fontSize: 'clamp(40px, 7vw, 96px)',
            letterSpacing: '-0.035em',
            lineHeight: 1.02,
            color: 'var(--paper)',
            margin: '0 0 12px',
            maxWidth: '20ch',
            marginInline: 'auto',
          }}
        >
          <span style={{ display: 'block' }}>{HERO_COPY.triplet[0]}</span>
          <span style={{ display: 'block' }}>{HERO_COPY.triplet[1]}</span>
          <span className="v4-sp-halo" style={{ display: 'block' }}>
            Your business is always{' '}
            <em
              style={{
                fontFamily: 'var(--font-instrument)',
                fontStyle: 'italic',
                color: 'var(--cyan-glow)',
                fontWeight: 400,
                letterSpacing: '-0.015em',
              }}
            >
              on
            </em>
            .
          </span>
        </h1>

        <p
          className="v4-sp-rise-4"
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 'clamp(16px, 2vw, 20px)',
            lineHeight: 1.55,
            color: 'rgba(244, 244, 240, 0.80)',
            maxWidth: '60ch',
            margin: '40px auto 0',
          }}
        >
          {HERO_COPY.ledePrimary}
        </p>

        <p
          className="v4-sp-rise-4"
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 15,
            lineHeight: 1.55,
            color: 'var(--dim)',
            maxWidth: '50ch',
            margin: '20px auto 0',
          }}
        >
          {HERO_COPY.ledeSecondary}
        </p>

        <div
          className="v4-sp-rise-4"
          style={{
            marginTop: 48,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            flexWrap: 'wrap',
            position: 'relative',
          }}
        >
          <Link
            ref={primaryCtaRef}
            href={SIMPLE_SETUP_COPY.ctaPrimary.href}
            rel="nofollow"
            className="v4-sp-magnetic"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              minWidth: 280,
              padding: '14px 28px',
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
              background: 'linear-gradient(135deg, var(--cyan-glow) 0%, var(--paper) 100%)',
              borderRadius: 9999,
              border: '1px solid rgba(125,250,255,0.4)',
              boxShadow: '0 18px 48px -12px rgba(34,211,238,0.55)',
              textDecoration: 'none',
            }}
          >
            {SIMPLE_SETUP_COPY.ctaPrimary.label}
            <ArrowRight className="h-4 w-4" />
          </Link>

          <V4GhostButton href="#how-it-works" variant="cyan" size="md">
            See how it works
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </V4GhostButton>

          <span
            aria-hidden="true"
            className="v4-sp-tether"
            style={{
              left: '50%',
              transform: 'translateX(-50%)',
              top: 'calc(100% + 8px)',
              height: 56,
            }}
          />
        </div>

        <p
          className="v4-sp-rise-5"
          style={{
            marginTop: 80,
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 12,
            color: 'var(--dim)',
            letterSpacing: '0.04em',
          }}
        >
          {HERO_COPY.trialMicrocopy}
        </p>
        <p
          className="v4-sp-rise-5"
          style={{
            marginTop: 12,
            fontFamily: 'var(--font-geist-mono)',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.22em',
            color: 'rgba(125, 250, 255, 0.80)',
          }}
        >
          {HERO_COPY.brandStamp}
        </p>
      </div>
    </section>
  );
}
